import React from 'react';
import { Plus, Trash2, GitBranch } from 'lucide-react';
import { Button } from '../../ui';

interface VariableSelectProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const VariableSelect: React.FC<VariableSelectProps> = ({ 
  label, 
  value, 
  onChange, 
  placeholder,
}) => {
  return (
    <div className="relative w-full">
      {label && <label className="block text-xs font-medium text-slate-700 mb-1">{label}</label>}
      <input
        type="text"
        className="w-full px-2 py-1.5 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
        placeholder={placeholder || 'Digite ou insira variável'}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
};

interface ConditionConfigProps {
  config: any;
  onChange: (key: string, value: any) => void;
}

export const ConditionConfig: React.FC<ConditionConfigProps> = ({ config, onChange }) => {
  const conditions = config.conditions || [];

  const addCondition = () => {
    const newCondition = {
      id: crypto.randomUUID(),
      name: `Caminho ${conditions.length + 1}`,
      logicOperator: 'AND',
      rules: [
        { variable: '', operator: 'equals', value: '' }
      ]
    };
    onChange('conditions', [...conditions, newCondition]);
  };

  const removeCondition = (index: number) => {
    const newConditions = [...conditions];
    newConditions.splice(index, 1);
    onChange('conditions', newConditions);
  };

  const updateConditionLogic = (index: number, logic: 'AND' | 'OR') => {
    const newConditions = [...conditions];
    newConditions[index].logicOperator = logic;
    onChange('conditions', newConditions);
  };

  const updateConditionName = (index: number, name: string) => {
    const newConditions = [...conditions];
    newConditions[index] = {
      ...newConditions[index],
      name,
    };
    onChange('conditions', newConditions);
  };

  const addRule = (conditionIndex: number) => {
    const newConditions = [...conditions];
    if (!newConditions[conditionIndex].rules) {
        newConditions[conditionIndex].rules = [];
    }
    newConditions[conditionIndex].rules.push({ variable: '', operator: 'equals', value: '' });
    onChange('conditions', newConditions);
  };

  const removeRule = (conditionIndex: number, ruleIndex: number) => {
    const newConditions = [...conditions];
    newConditions[conditionIndex].rules.splice(ruleIndex, 1);
    onChange('conditions', newConditions);
  };

  const updateRule = (conditionIndex: number, ruleIndex: number, field: string, value: any) => {
    const newConditions = [...conditions];
    const updatedRule = {
      ...newConditions[conditionIndex].rules[ruleIndex],
      [field]: value,
    };
    // Operators "Vazio" / "N/Vazio" don't compare against a value — clear any leftover value.
    if (field === 'operator' && (value === 'is_empty' || value === 'is_not_empty')) {
      updatedRule.value = '';
    }
    newConditions[conditionIndex].rules[ruleIndex] = updatedRule;
    onChange('conditions', newConditions);
  };

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700 leading-relaxed">
        <strong>Como funciona:</strong> Avalia condições sobre variáveis e direciona o fluxo pelo caminho correspondente. Cada caminho pode ter múltiplas regras combinadas com E/OU. Se nenhuma condição for verdadeira, segue pela saída "Senão". As comparações de texto ignoram maiúsculas/minúsculas.
      </div>
      <div className="space-y-4">
        {conditions.map((condition: any, idx: number) => (
          <div key={condition.id} className="border border-slate-200 rounded-lg p-3 bg-white shadow-sm">
            <div className="flex items-center justify-between mb-2 pb-2 border-b border-slate-100">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                    <GitBranch className="w-4 h-4 text-blue-500" />
                    <input
                        type="text"
                        className="w-full text-sm font-medium text-slate-700 bg-transparent border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        value={condition.name || ''}
                        onChange={(e) => updateConditionName(idx, e.target.value)}
                        placeholder={`Caminho ${idx + 1}`}
                        title="Nome do caminho"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <select
                        className="text-xs border-slate-200 rounded px-1 py-0.5 bg-slate-50 text-slate-600 focus:ring-1 focus:ring-blue-500"
                        value={condition.logicOperator || 'AND'}
                        onChange={(e) => updateConditionLogic(idx, e.target.value as any)}
                        title="Lógica entre as regras"
                    >
                        <option value="AND">E (Todas)</option>
                        <option value="OR">OU (Qualquer)</option>
                    </select>
                    <button onClick={() => removeCondition(idx)} className="text-slate-400 hover:text-red-500">
                        <Trash2 className="w-4 h-4" />
                    </button>
                </div>
            </div>

            <div className="space-y-2">
                {(condition.rules || []).map((rule: any, ruleIdx: number) => (
                    <div key={ruleIdx} className="flex gap-2 items-start bg-slate-50 p-2 rounded border border-slate-100 relative group">
                        <div className="flex-1 min-w-0 grid grid-cols-1 gap-1">
                            {/* Variable */}
                            <VariableSelect
                                value={rule.variable}
                                onChange={(val) => updateRule(idx, ruleIdx, 'variable', val)}
                                placeholder="Variável"
                            />
                            
                            <div className="flex gap-1">
                                {/* Operator — "Vazio"/"N/Vazio" need no value, so the select takes full width */}
                                {(() => {
                                  const noValueNeeded = rule.operator === 'is_empty' || rule.operator === 'is_not_empty';
                                  return (
                                    <>
                                      <select
                                          className={`${noValueNeeded ? 'w-full' : 'w-1/3'} text-xs border border-slate-300 rounded px-1 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500`}
                                          value={rule.operator}
                                          onChange={(e) => updateRule(idx, ruleIdx, 'operator', e.target.value)}
                                      >
                                          <option value="equals">Igual</option>
                                          <option value="not_equals">Diferente</option>
                                          <option value="contains">Contém</option>
                                          <option value="not_contains">N/Contém</option>
                                          <option value="starts_with">Começa</option>
                                          <option value="ends_with">Termina</option>
                                          <option value="greater_than">Maior</option>
                                          <option value="less_than">Menor</option>
                                          <option value="is_empty">Vazio</option>
                                          <option value="is_not_empty">N/Vazio</option>
                                          <option value="regex">Regex</option>
                                      </select>

                                      {/* Value — hidden when the operator doesn't compare against a value */}
                                      {!noValueNeeded && (
                                        <div className="w-2/3">
                                            <VariableSelect
                                                value={rule.value}
                                                onChange={(val) => updateRule(idx, ruleIdx, 'value', val)}
                                                placeholder="Valor"
                                            />
                                        </div>
                                      )}
                                    </>
                                  );
                                })()}
                            </div>
                        </div>

                        {/* Remove Rule Button */}
                        <button 
                            onClick={() => removeRule(idx, ruleIdx)}
                            className="mt-1 text-slate-300 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                            <X size={14} />
                        </button>
                    </div>
                ))}

                <button
                    onClick={() => addRule(idx)}
                    className="w-full py-1 text-xs text-blue-600 bg-blue-50 hover:bg-blue-100 rounded border border-blue-100 border-dashed flex items-center justify-center gap-1 transition-colors"
                >
                    <Plus className="w-3 h-3" />
                    Adicionar Regra
                </button>
            </div>
          </div>
        ))}
      </div>

      <Button
        variant="outline"
        className="w-full border-dashed"
        onClick={addCondition}
      >
        <Plus className="w-4 h-4 mr-2" />
        Novo Caminho Condicional
      </Button>

      <div className="space-y-1">
        <label className="text-xs font-medium text-slate-700">Nome do caminho padrão (Senão)</label>
        <input
          type="text"
          className="w-full px-2 py-1.5 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
          value={config.defaultLabel || ''}
          onChange={(e) => onChange('defaultLabel', e.target.value)}
          placeholder="Padrão"
        />
      </div>

      {/* Fallback explanation */}
      <div className="text-[10px] text-slate-400 text-center">
        Se nenhuma condição for atendida, o fluxo seguirá pelo "Padrão".
      </div>
    </div>
  );
};

function X(props: any) {
    return <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
}
